/**
 * Async Okta org-audit with progress polling (POST-ARC.11b).
 *
 * @module client/utils/oktaOrgAuditAsync
 */

const POLL_MS = 2000;
const MAX_POLL_MS = 600_000;

/**
 * @param {{
 *   orgUrl: string,
 *   apiToken: string,
 *   projectId: string,
 *   trigger?: string,
 *   onProgress?: (payload: { steps?: unknown[], phaseLabel?: string }) => void,
 * }} opts
 */
export async function runOktaOrgAuditAsync(opts) {
  const startRes = await fetch("/api/idp/okta/org-audit", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      orgUrl: opts.orgUrl,
      apiToken: opts.apiToken,
      projectId: opts.projectId,
      trigger: opts.trigger || "manual",
      async: true,
    }),
  });
  const startData = await startRes.json().catch(() => ({}));
  if (!startRes.ok || startData.ok === false) {
    throw new Error(startData.error || "Failed to start org-audit");
  }
  const runId = startData.runId;
  if (!runId) throw new Error("Org-audit did not return runId");

  const started = Date.now();
  while (Date.now() - started < MAX_POLL_MS) {
    await new Promise((r) => setTimeout(r, POLL_MS));
    const statusRes = await fetch(
      `/api/idp/okta/org-audit/runs/${encodeURIComponent(runId)}`,
      { credentials: "include" },
    );
    const statusData = await statusRes.json().catch(() => ({}));
    if (!statusRes.ok || statusData.ok === false) {
      throw new Error(statusData.error || "Failed to poll org-audit status");
    }
    opts.onProgress?.({
      steps: statusData.steps,
      phaseLabel: statusData.phaseLabel,
      fanoutCompleted: statusData.fanoutCompleted,
      fanoutTotal: statusData.fanoutTotal,
    });
    if (statusData.status === "complete") {
      return { audit: statusData.audit, drift: statusData.drift || null };
    }
    if (statusData.status === "failed") {
      const err = new Error(statusData.error || "Org-audit failed");
      if (statusData.errorCode) err.code = statusData.errorCode;
      throw err;
    }
  }
  const err = new Error(
    "Org-audit timed out while waiting for completion. Use your latest snapshot for demos or retry later.",
  );
  err.code = "ORG_AUDIT_TIMEOUT";
  throw err;
}
